import { useCallback, useEffect } from 'react';

import { useMessage, useMyStore, useQuery } from '../hooks';

import { GET_GROUP_AFFILIATION_PROMOTION } from '../../graphql/group-affiliation-promotion-queries';

import { trackAction } from '../../utils/analytics/analytics';
import { localStorage as ls } from '../../utils/storage/storage';

import { IDME_AD_BLOCKER_MSG } from '../../../shared/constants/messages';
import {
  IDME_IS_VERIFIED,
  IDME_VERIFIED_GROUP
} from '../../constants/storage-keys';

export default function useGroupAffiliation({ onRefresh } = {}) {
  const { myStore } = useMyStore();
  const { data, isLoading, refetch } = useQuery(
    GET_GROUP_AFFILIATION_PROMOTION,
    {
      fetchPolicy: 'network-only',
      skip: !myStore,
      variables: { storeCode: myStore?.code }
    }
  );
  const messages = useMessage([IDME_AD_BLOCKER_MSG]);
  const { groupAffiliationPromotion } = data?.promotion || {};
  const { promotion } = groupAffiliationPromotion || {};
  const isAppliedPromotion =
    groupAffiliationPromotion?.applied ||
    groupAffiliationPromotion?.willBeApplied;
  const scope = groupAffiliationPromotion?.eligibleGroups
    ?.map(group => group.toLowerCase())
    .join(',');
  const refreshGroupAffiliationData = useCallback(
    error => {
      if (error) {
        trackAction('idme_login_error', error);
        console.error(error);
      } else {
        trackAction('idme_login_success');
        ls(IDME_IS_VERIFIED, true);
        ls(IDME_VERIFIED_GROUP, groupAffiliationPromotion?.verifiedGroup);
      }

      window.refreshGroupAffiliationData = undefined;
      onRefresh?.();
      refetch();
    },
    [groupAffiliationPromotion, onRefresh, refetch]
  );

  const openVerificationWindow = useCallback(() => {
    const callbackUrl = `${window.location.origin}/idme/callback`;
    const clientID = window.isAT
      ? process.env.__AT_IDME_CLIENT_ID__
      : process.env.__DT_IDME_CLIENT_ID__;
    const query = `client_id=${clientID}&redirect_uri=${callbackUrl}&response_type=code&scopes=${scope}`;
    const url = `${process.env.__IDME_API_URL__}?${query}`;
    const width = Math.min(775, window.innerWidth);
    const height = Math.min(850, window.innerHeight);

    trackAction('idme_modal_open');
    window.refreshGroupAffiliationData = refreshGroupAffiliationData;
    window.open(url, 'verify', `width=${width}, height=${height}`);
  }, [refreshGroupAffiliationData, scope]);

  useEffect(() => {
    if (!isLoading) {
      ls(IDME_IS_VERIFIED, Boolean(isAppliedPromotion));
      ls(
        IDME_VERIFIED_GROUP,
        isAppliedPromotion ? groupAffiliationPromotion?.verifiedGroup : void 0
      );
    }
  }, [groupAffiliationPromotion, isLoading, isAppliedPromotion]);

  return {
    groupAffiliationPromotion,
    idmeAdBlockerMessage: messages?.IDME_AD_BLOCKER_MSG,
    isAppliedPromotion,
    openVerificationWindow,
    promotion
  };
}
